import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { restaurantAPI } from '../../services/api';
import useLocation from '../../hooks/useLocation';

// Flutter App Theme Colors (exact match)
const theme = {
  primaryColor: '#FF6961',
  primaryLight: '#FF8A84',
  primaryDark: '#E6524A',
  backgroundColor: '#F8FAFC',
  surfaceColor: '#FFFFFF',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  successColor: '#22C55E',
  warningColor: '#F59E0B',
  errorColor: '#EF4444',
  infoColor: '#3B82F6',
};

// Available cuisine types for selection
const CUISINE_TYPES = [
  'North Indian',
  'South Indian',
  'Chinese',
  'Italian',
  'Mexican',
  'Continental',
  'Fast Food',
  'Street Food',
  'Desserts',
  'Beverages'
];

function RestaurantProfile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAddressFromCurrentLocation, loading: locationLoading } = useLocation();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cuisineType: [],
    phone: '',
    contactPhone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    lat: '',
    lng: '',
    avgDeliveryTime: '',
    fssaiLicense: '',
    gstin: ''
  });
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [uploadingMenuImage, setUploadingMenuImage] = useState(false);
  const [uploadingRestaurantPhoto, setUploadingRestaurantPhoto] = useState(false);

  useEffect(() => {
    fetchRestaurant();
    fetchPlanStatus();

    // Clear refresh parameter if present
    if (searchParams.get('refresh')) {
      setSearchParams({});
    }
  }, [searchParams.get('refresh')]);

  const fetchRestaurant = async () => {
    try {
      const response = await restaurantAPI.getMyRestaurant();
      const rest = response.data.data.restaurant;
      setRestaurant(rest);
      setFormData({
        name: rest.name || '',
        description: rest.description || '',
        cuisineType: rest.cuisineType || [],
        phone: rest.phone || '',
        contactPhone: rest.contactPhone || '',
        email: rest.email || '',
        street: rest.address?.street || '',
        city: rest.address?.city || '',
        state: rest.address?.state || '',
        zipCode: rest.address?.zipCode || '',
        lat: rest.location?.lat || '',
        lng: rest.location?.lng || '',
        avgDeliveryTime: rest.deliveryTimings?.estimatedDeliveryTime || '',
        fssaiLicense: rest.documents?.fssai?.number || '',
        gstin: rest.documents?.gst?.number || ''
      });
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanStatus = async () => {
    try {
      const response = await restaurantAPI.getSubscriptionPlanStatus();
      const plan = response.data.data.subscriptionPlan;
      setPlanStatus(plan);
    } catch (error) {
      console.error('Failed to fetch plan status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate cuisine type selection
    if (formData.cuisineType.length === 0) {
      alert('Please select at least one cuisine type');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        cuisineType: formData.cuisineType,
        phone: formData.phone,
        contactPhone: formData.contactPhone,
        email: formData.email,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        },
        location: {
          lat: parseFloat(formData.lat) || 0,
          lng: parseFloat(formData.lng) || 0
        },
        deliveryTimings: {
          estimatedDeliveryTime: parseInt(formData.avgDeliveryTime) || 30
        },
        documents: {
          fssai: {
            number: formData.fssaiLicense
          },
          gst: {
            number: formData.gstin
          }
        }
      };

      await restaurantAPI.update(restaurant._id, updateData);
      alert('Restaurant profile updated successfully!');
      setEditing(false);
      fetchRestaurant();
    } catch (error) {
      alert('Failed to update profile: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: restaurant.name || '',
      description: restaurant.description || '',
      cuisineType: restaurant.cuisineType || [],
      phone: restaurant.phone || '',
      contactPhone: restaurant.contactPhone || '',
      email: restaurant.email || '',
      street: restaurant.address?.street || '',
      city: restaurant.address?.city || '',
      state: restaurant.address?.state || '',
      zipCode: restaurant.address?.zipCode || '',
      lat: restaurant.location?.lat || '',
      lng: restaurant.location?.lng || '',
      avgDeliveryTime: restaurant.deliveryTimings?.estimatedDeliveryTime || '',
      fssaiLicense: restaurant.documents?.fssai?.number || '',
      gstin: restaurant.documents?.gst?.number || ''
    });
    setEditing(false);
  };

  // Toggle cuisine type selection
  const toggleCuisineType = (cuisine) => {
    setFormData(prev => {
      const isSelected = prev.cuisineType.includes(cuisine);
      return {
        ...prev,
        cuisineType: isSelected
          ? prev.cuisineType.filter(c => c !== cuisine)
          : [...prev.cuisineType, cuisine]
      };
    });
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingProfileImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('imageType', 'profile');

    try {
      await restaurantAPI.uploadImage(formDataUpload);
      alert('Profile image uploaded successfully!');
      fetchRestaurant();
    } catch (error) {
      alert('Failed to upload image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingProfileImage(false);
    }
  };

  // Handle menu image upload
  const handleMenuImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingMenuImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('imageType', 'menu');

    try {
      await restaurantAPI.uploadImage(formDataUpload);
      alert('Menu image uploaded successfully!');
      fetchRestaurant();
    } catch (error) {
      alert('Failed to upload image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingMenuImage(false);
    }
  };

  // Handle restaurant photo upload
  const handleRestaurantPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingRestaurantPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('imageType', 'photo');

    try {
      await restaurantAPI.uploadImage(formDataUpload);
      alert('Restaurant photo uploaded successfully!');
      fetchRestaurant();
    } catch (error) {
      alert('Failed to upload photo: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingRestaurantPhoto(false);
    }
  };

  // Handle image deletion
  const handleDeleteImage = async (imageUrl, imageType) => {
    if (!window.confirm(`Are you sure you want to delete this ${imageType} image?`)) return;

    try {
      await restaurantAPI.deleteImage(imageUrl);
      alert('Image deleted successfully!');
      fetchRestaurant();
    } catch (error) {
      alert('Failed to delete image: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDetectLocation = async () => {
    try {
      // First, get GPS coordinates (this always works if permission granted)
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      // Try to get address details (but don't fail if this doesn't work)
      let addressDetails = null;
      try {
        addressDetails = await getAddressFromCurrentLocation();
      } catch (addressError) {
        console.warn('Could not get address details, but GPS coordinates were obtained:', addressError);
      }

      // Update form with GPS coordinates and address if available
      setFormData(prev => ({
        ...prev,
        ...(addressDetails && {
          street: addressDetails.street || prev.street,
          city: addressDetails.city || prev.city,
          state: addressDetails.state || prev.state,
          zipCode: addressDetails.zipCode || prev.zipCode,
        }),
        lat: lat,
        lng: lng
      }));

      if (addressDetails) {
        alert('Location and GPS coordinates detected successfully!');
      } else {
        alert('GPS coordinates detected successfully!\n\nNote: Could not auto-detect address details. Please enter your street address, city, state, and ZIP code manually.');
      }
    } catch (error) {
      let errorMessage = 'Failed to detect location. ';
      if (error.code === 1) {
        errorMessage += 'Please enable location permissions in your browser settings.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out.';
      } else {
        errorMessage += error.message || 'Please ensure location permissions are enabled.';
      }
      alert(errorMessage);
    }
  };

  // Get first letter of restaurant name for avatar
  const getInitial = () => {
    return restaurant?.name?.charAt(0)?.toUpperCase() || 'R';
  };

  // Calculate average rating
  const getAverageRating = () => {
    return restaurant?.rating || 0;
  };

  // Get review count
  const getReviewCount = () => {
    return restaurant?.totalReviews || 0;
  };

  if (loading) return <div className="loading">Loading profile...</div>;

  // If editing mode, show edit form
  if (editing) {
    return (
      <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
        <div className="container" style={{ maxWidth: '900px', padding: '20px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: theme.textPrimary,
                margin: 0
              }}>
                Edit Restaurant Information
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Restaurant Profile Image */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Restaurant Profile Image
                </h3>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(12px, 3vw, 20px)',
                  padding: 'clamp(12px, 3vw, 16px)',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '1px dashed #CBD5E1',
                  flexWrap: 'wrap'
                }}>
                  {/* Current Profile Image */}
                  <div style={{
                    width: 'clamp(80px, 20vw, 120px)',
                    height: 'clamp(80px, 20vw, 120px)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: 'white',
                    border: '2px solid #E5E7EB',
                    flexShrink: 0
                  }}>
                    {restaurant?.photos?.[0] ? (
                      <img
                        src={restaurant.photos[0]}
                        alt="Restaurant"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${theme.primaryColor}15`,
                        color: theme.primaryColor,
                        fontSize: '48px',
                        fontWeight: '700'
                      }}>
                        {getInitial()}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: theme.textSecondary }}>
                      Upload your restaurant logo or profile image
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <label style={{
                        padding: '8px 16px',
                        background: theme.primaryColor,
                        color: 'white',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        {uploadingProfileImage ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfileImageUpload}
                          disabled={uploadingProfileImage}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {restaurant?.photos?.[0] && (
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(restaurant.photos[0], 'profile')}
                          style={{
                            padding: '8px 16px',
                            background: 'transparent',
                            border: `1px solid ${theme.errorColor}`,
                            color: theme.errorColor,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
                      Max 5MB, JPG/PNG recommended
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu/Gallery Images */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Menu & Gallery Images
                </h3>

                {/* Existing Images Grid */}
                {restaurant?.menuImages && restaurant.menuImages.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    {restaurant.menuImages.map((img, index) => (
                      <div key={index} style={{
                        position: 'relative',
                        paddingBottom: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: '#E5E7EB'
                      }}>
                        <img
                          src={img}
                          alt={`Menu ${index + 1}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img, 'menu')}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '32px',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '2px dashed #CBD5E1',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <span style={{ fontSize: '24px' }}>📷</span>
                  <span style={{ color: theme.textSecondary }}>
                    {uploadingMenuImage ? 'Uploading...' : 'Add Menu/Gallery Images'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMenuImageUpload}
                    disabled={uploadingMenuImage}
                    style={{ display: 'none' }}
                  />
                </label>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.textSecondary, textAlign: 'center' }}>
                  Add images of your menu items, restaurant interior, or special dishes
                </p>
              </div>

              {/* Restaurant Photos */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Restaurant Photos
                </h3>

                {/* Existing Photos Grid (excluding first photo which is profile/logo) */}
                {restaurant?.photos && restaurant.photos.length > 1 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    {restaurant.photos.slice(1).map((img, index) => (
                      <div key={index} style={{
                        position: 'relative',
                        paddingBottom: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: '#E5E7EB'
                      }}>
                        <img
                          src={img}
                          alt={`Restaurant ${index + 1}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img, 'photo')}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '32px',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '2px dashed #CBD5E1',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <span style={{ fontSize: '24px' }}>🏪</span>
                  <span style={{ color: theme.textSecondary }}>
                    {uploadingRestaurantPhoto ? 'Uploading...' : 'Add Restaurant Photos'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleRestaurantPhotoUpload}
                    disabled={uploadingRestaurantPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.textSecondary, textAlign: 'center' }}>
                  Add photos of your restaurant exterior, ambiance, or seating area
                </p>
              </div>

              {/* Basic Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Basic Information
                </h3>

                <div className="form-group">
                  <label>Restaurant Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your restaurant, specialties, ambiance..."
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>Cuisine Type</label>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginTop: '8px'
                  }}>
                    {CUISINE_TYPES.map((cuisine) => {
                      const isSelected = formData.cuisineType.includes(cuisine);
                      return (
                        <button
                          key={cuisine}
                          type="button"
                          onClick={() => toggleCuisineType(cuisine)}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '20px',
                            border: '1px solid #E5E7EB',
                            background: isSelected ? `${theme.primaryColor}15` : 'white',
                            color: theme.textPrimary,
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {isSelected && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.primaryColor} strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {cuisine}
                        </button>
                      );
                    })}
                  </div>
                  {formData.cuisineType.length === 0 && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.errorColor }}>
                      Please select at least one cuisine type
                    </p>
                  )}
                </div>
              </div>

              {/* Operational Details */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Operational Details
                </h3>

                <div className="form-group">
                  <label>Avg. Delivery Time (minutes)</label>
                  <input
                    type="number"
                    min="10"
                    max="120"
                    value={formData.avgDeliveryTime}
                    onChange={(e) => setFormData({ ...formData, avgDeliveryTime: e.target.value })}
                    onWheel={(e) => e.target.blur()}
                    placeholder="30"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Contact Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#666' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#666' }}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label>Contact Number (shown on invoice &amp; used for customer/delivery calls)</label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="e.g. 9876543210 — leave blank to use your registered number"
                  />
                  <small style={{ color: theme.textSecondary || '#888', display: 'block', marginTop: '4px' }}>
                    Optional front-desk number. Masked calls ring this instead of your login number, and it prints on invoices/receipts. Leave blank to keep using your registered number.
                  </small>
                </div>
              </div>

              {/* Address Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  marginBottom: '16px'
                }}>
                  Address
                </h3>

                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={locationLoading}
                  className="btn btn-secondary"
                  style={{
                    width: '100%',
                    marginBottom: '16px'
                  }}
                >
                  {locationLoading ? 'Detecting Location...' : '📍 Use Current Location'}
                </button>

                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    required
                  />
                </div>

                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>GPS Coordinates</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.lat}
                        onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                        onWheel={(e) => e.target.blur()}
                        placeholder="27.1751"
                      />
                    </div>

                    <div className="form-group">
                      <label>Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.lng}
                        onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                        onWheel={(e) => e.target.blur()}
                        placeholder="78.0421"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Menu Item Component - matches Flutter _MenuItem
  const MenuItem = ({ icon, title, subtitle, onClick, to }) => {
    const content = (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        <div
          onClick={onClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            cursor: 'pointer',
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
        >
          {/* Icon Container - All use primary color like Flutter */}
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: `${theme.primaryColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            flexShrink: 0
          }}>
            <span style={{ fontSize: '22px', color: theme.primaryColor }}>{icon}</span>
          </div>

          {/* Text Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontWeight: '500',
              color: theme.textPrimary,
              fontSize: '16px'
            }}>
              {title}
            </p>
            <p style={{
              margin: '2px 0 0 0',
              fontSize: '13px',
              color: theme.textSecondary
            }}>
              {subtitle}
            </p>
          </div>

          {/* Chevron */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    );

    if (to) {
      return <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link>;
    }
    return content;
  };

  // Main Profile View - matches Flutter ProfileScreen
  return (
    <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
      {/* Header with Gradient Background - matches Flutter _buildHeader */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
        padding: '24px 20px 32px',
        textAlign: 'center',
        color: 'white'
      }}>
        {/* Spacer for status bar */}
        <div style={{ height: '20px' }} />

        {/* Profile Image Box */}
        <div style={{
          width: 'clamp(80px, 20vw, 100px)',
          height: 'clamp(80px, 20vw, 100px)',
          borderRadius: '20px',
          background: 'white',
          overflow: 'hidden',
          margin: '0 auto 16px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)'
        }}>
          {restaurant?.photos?.[0] ? (
            <img
              src={restaurant.photos[0]}
              alt={restaurant.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white'
            }}>
              <span style={{
                fontSize: '48px',
                fontWeight: '700',
                color: theme.primaryColor
              }}>
                {getInitial()}
              </span>
            </div>
          )}
        </div>

        {/* Restaurant Name */}
        <h1 style={{
          fontSize: '22px',
          fontWeight: '700',
          margin: '0 0 8px 0',
          color: 'white'
        }}>
          {restaurant?.name}
        </h1>

        {/* Rating Row - matches Flutter Row with star icon */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          marginBottom: '8px'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={theme.warningColor}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>
            {getAverageRating().toFixed(1)}
          </span>
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
            ({getReviewCount()} reviews)
          </span>
        </div>

        {/* Location Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          marginBottom: '8px'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
            {restaurant?.address?.city}, {restaurant?.address?.state}
          </span>
        </div>

        {/* Cuisine Types */}
        {restaurant?.cuisineType && restaurant.cuisineType.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            {restaurant.cuisineType.slice(0, 3).map((cuisine, index) => (
              <span
                key={index}
                style={{
                  padding: '4px 10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: 'white',
                  fontWeight: '500'
                }}
              >
                {cuisine}
              </span>
            ))}
            {restaurant.cuisineType.length > 3 && (
              <span style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.7)'
              }}>
                +{restaurant.cuisineType.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Settings Section - matches Flutter _buildMenuItems */}
      <div style={{ padding: '24px 16px' }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '700',
          color: theme.textPrimary,
          marginBottom: '12px'
        }}>
          Settings
        </h2>

        {/* Menu Items - Order matches Flutter exactly */}
        <MenuItem
          icon="✏️"
          title="Edit Profile"
          subtitle="Update restaurant details"
          onClick={() => setEditing(true)}
        />

        <MenuItem
          icon="⭐"
          title="Reviews"
          subtitle="View and respond to reviews"
          to="/restaurant/reviews"
        />

        <MenuItem
          icon="🏷️"
          title="Coupons"
          subtitle="Manage discount coupons"
          to="/restaurant/coupons"
        />

        <MenuItem
          icon="🕐"
          title="Opening Hours"
          subtitle="Set your working hours"
          to="/restaurant/opening-hours"
        />

        <MenuItem
          icon="🏦"
          title="Bank Details"
          subtitle="Manage bank account for payouts"
          to="/restaurant/bank-details"
        />

        <MenuItem
          icon="🔔"
          title="Notifications"
          subtitle="Manage notification settings"
          to="/restaurant/notification-settings"
        />

        <MenuItem
          icon="💳"
          title="Subscription"
          subtitle="View and manage your plan"
          to="/restaurant/subscription-info"
        />

        <MenuItem
          icon="🚀"
          title="Premium Add-ons"
          subtitle="Boost visibility & get more orders"
          to="/restaurant/addon-subscriptions"
        />

        <MenuItem
          icon="💰"
          title="Earnings"
          subtitle="View your earnings and payouts"
          to="/restaurant/earnings"
        />

        <MenuItem
          icon="🧾"
          title="Invoices"
          subtitle="View subscription invoices"
          to="/restaurant/invoices"
        />

        <MenuItem
          icon="❓"
          title="Help & Support"
          subtitle="Get help with your issues"
          to="/restaurant/help-support"
        />

        <MenuItem
          icon="ℹ️"
          title="About"
          subtitle="App version 1.0.0"
          to="/restaurant/about"
        />
      </div>

      {/* Logout Button - matches Flutter _buildLogoutButton */}
      <div style={{ padding: '0 16px 24px' }}>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to logout?')) {
              localStorage.removeItem('token');
              localStorage.removeItem('userType');
              localStorage.removeItem('restaurantId');
              window.location.href = '/login';
            }
          }}
          style={{
            width: '100%',
            padding: '14px',
            background: 'transparent',
            border: `1px solid ${theme.errorColor}`,
            borderRadius: '8px',
            color: theme.errorColor,
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#FEE2E2';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.errorColor} strokeWidth="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="16,17 21,12 16,7" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="21" y1="12" x2="9" y2="12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Logout
        </button>
      </div>
    </div>
  );
}

export default RestaurantProfile;
